// SPDX-License-Identifier: AGPL-3.0-or-later

import {GuildFeatures} from '@fluxer/constants/src/GuildConstants';

/** How many guild boost slots each premium (Plutonium/Visionary) member receives. */
export const GUILD_BOOST_SLOTS_PER_PREMIUM_USER = 2;

/** Boost count required to reach each level (inclusive). */
export const GUILD_BOOST_LEVEL_THRESHOLDS = {
	1: 2,
	2: 8,
	3: 14,
} as const;

export type GuildBoostLevel = 0 | 1 | 2 | 3;

/** Sticker slot caps by boost level. Level 0 is the guild default without boosts. */
export const GUILD_BOOST_STICKER_LIMITS: Record<GuildBoostLevel, number> = {
	0: 5,
	1: 15,
	2: 30,
	3: 60,
};

/**
 * Features managed exclusively by the guild boost ladder.
 * These must not be in BASE_GUILD_FEATURES; they are applied by GuildBoostService.
 */
export const GUILD_BOOST_MANAGED_FEATURES: ReadonlyArray<string> = [
	GuildFeatures.AUDIO_BITRATE_128_KBPS,
	GuildFeatures.AUDIO_BITRATE_256_KBPS,
	GuildFeatures.AUDIO_BITRATE_384_KBPS,
	GuildFeatures.ANIMATED_ICON,
	GuildFeatures.BANNER,
	GuildFeatures.INVITE_SPLASH,
	GuildFeatures.ANIMATED_BANNER,
	GuildFeatures.VANITY_URL,
	GuildFeatures.SERVER_TAG,
];

/**
 * Boost-managed features that reconciliation never strips.
 * Admins may grant these manually, and losing boosts must not revoke them.
 */
export const GUILD_BOOST_STICKY_FEATURES: ReadonlyArray<string> = [GuildFeatures.SERVER_TAG];

/** Cumulative features unlocked at each boost level. */
export const GUILD_BOOST_LEVEL_FEATURES: Record<Exclude<GuildBoostLevel, 0>, ReadonlyArray<string>> = {
	1: [GuildFeatures.AUDIO_BITRATE_128_KBPS, GuildFeatures.ANIMATED_ICON],
	2: [
		GuildFeatures.AUDIO_BITRATE_128_KBPS,
		GuildFeatures.AUDIO_BITRATE_256_KBPS,
		GuildFeatures.ANIMATED_ICON,
		GuildFeatures.BANNER,
		GuildFeatures.INVITE_SPLASH,
		GuildFeatures.SERVER_TAG,
	],
	3: [
		GuildFeatures.AUDIO_BITRATE_128_KBPS,
		GuildFeatures.AUDIO_BITRATE_256_KBPS,
		GuildFeatures.AUDIO_BITRATE_384_KBPS,
		GuildFeatures.ANIMATED_ICON,
		GuildFeatures.BANNER,
		GuildFeatures.INVITE_SPLASH,
		GuildFeatures.SERVER_TAG,
		GuildFeatures.ANIMATED_BANNER,
		GuildFeatures.VANITY_URL,
	],
};

export function computeGuildBoostLevelFromCount(boostCount: number): GuildBoostLevel {
	const count = Number.isFinite(boostCount) ? Math.max(0, Math.floor(boostCount)) : 0;
	if (count >= GUILD_BOOST_LEVEL_THRESHOLDS[3]) return 3;
	if (count >= GUILD_BOOST_LEVEL_THRESHOLDS[2]) return 2;
	if (count >= GUILD_BOOST_LEVEL_THRESHOLDS[1]) return 1;
	return 0;
}

export function resolveEffectiveGuildBoostLevel(params: {
	boostCount: number;
	boostLevelOverride: number | null | undefined;
}): GuildBoostLevel {
	const override = params.boostLevelOverride;
	if (override === 0 || override === 1 || override === 2 || override === 3) {
		return override;
	}
	return computeGuildBoostLevelFromCount(params.boostCount);
}

export function getGuildBoostFeaturesForLevel(level: GuildBoostLevel): ReadonlySet<string> {
	if (level === 0) return new Set();
	return new Set(GUILD_BOOST_LEVEL_FEATURES[level]);
}

/** Features newly unlocked at this level (excludes perks already granted by lower levels). */
export function getGuildBoostFeaturesIntroducedAtLevel(
	level: Exclude<GuildBoostLevel, 0>,
): ReadonlyArray<string> {
	const current = GUILD_BOOST_LEVEL_FEATURES[level];
	if (level === 1) return [...current];
	const previous = new Set(GUILD_BOOST_LEVEL_FEATURES[(level - 1) as 1 | 2]);
	return current.filter((feature) => !previous.has(feature));
}

export function getGuildBoostStickerLimit(level: GuildBoostLevel): number {
	return GUILD_BOOST_STICKER_LIMITS[level];
}
